import {
  AlertCircle,
  ChevronDown,
  Crown,
  Eye,
  Loader2,
  Pencil,
  Search,
  Shield,
  Trash2,
  User,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EntityDetailHeader, TeamFormModal, type TeamFormValues, useConfirm, useToast } from "@/components/shared";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { authService, teamService } from "@/lib/services";
import type { TeamDetail as TeamDetailType, TeamMember, UserSearchResult } from "@/types";

const ROLE_OPTIONS = [
  { value: "team_admin", label: "Admin", icon: Crown, description: "Full team management access" },
  { value: "member", label: "Member", icon: User, description: "Can use team resources" },
  { value: "viewer", label: "Viewer", icon: Eye, description: "Read-only access" },
] as const;

function getRoleConfig(role: string) {
  switch (role) {
    case "team_admin":
      return { label: "Admin", color: "text-amber-400", bg: "bg-amber-500/20", icon: Crown };
    case "member":
      return { label: "Member", color: "text-blue-400", bg: "bg-blue-500/20", icon: User };
    case "viewer":
      return { label: "Viewer", color: "text-slate-400", bg: "bg-slate-500/20", icon: Eye };
    default:
      return { label: role, color: "text-slate-400", bg: "bg-slate-500/20", icon: User };
  }
}

export function TeamDetail() {
  const { user } = useAuth();

  if (!user?.isPlatformAdmin) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="h-16 w-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-content-primary">Access Denied</h2>
          <p className="mt-2 text-content-tertiary max-w-sm">
            You don't have permission to manage teams. Contact your administrator for access.
          </p>
        </div>
      </div>
    );
  }

  return <TeamDetailContent />;
}

function TeamDetailContent() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { refreshTeams } = useApp();

  const [team, setTeam] = useState<TeamDetailType | null>(null);
  const [loading, setLoading] = useState(true);

  // Edit team state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  // Add member state
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({ email: "", role: "member" });
  const [addingMember, setAddingMember] = useState(false);

  // User search state for add member
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Role dropdown state
  const [roleDropdownOpen, setRoleDropdownOpen] = useState<string | null>(null);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);

  const loadTeam = useCallback(async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      const response = await teamService.get(teamId);
      setTeam(response.data);
    } catch (error) {
      console.error("Failed to load team:", error);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  // Debounced user search for add member
  useEffect(() => {
    if (!userSearchQuery.trim() || userSearchQuery.length < 2) {
      setUserSearchResults([]);
      setShowUserDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearchingUsers(true);
        const response = await authService.searchUsers(userSearchQuery);
        // Filter out users who are already team members
        const existingMemberEmails = new Set((team?.members || []).map((m) => m.email.toLowerCase()));
        const filtered = response.data.filter((u) => !existingMemberEmails.has(u.email.toLowerCase()));
        setUserSearchResults(filtered);
        setShowUserDropdown(true);
      } catch (err) {
        console.error("Failed to search users:", err);
      } finally {
        setIsSearchingUsers(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [userSearchQuery, team?.members]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowUserDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectUser = (user: UserSearchResult) => {
    setSelectedUser(user);
    setAddMemberForm({ ...addMemberForm, email: user.email });
    setUserSearchQuery(user.name || user.email);
    setShowUserDropdown(false);
    setUserSearchResults([]);
  };

  const clearSelectedUser = () => {
    setSelectedUser(null);
    setAddMemberForm({ ...addMemberForm, email: "" });
    setUserSearchQuery("");
    searchInputRef.current?.focus();
  };

  const resetAddMemberState = () => {
    setShowAddMemberModal(false);
    setAddMemberForm({ email: "", role: "member" });
    setUserSearchQuery("");
    setUserSearchResults([]);
    setSelectedUser(null);
    setShowUserDropdown(false);
  };

  const handleEditTeam = async (values: TeamFormValues) => {
    if (!team) return;
    try {
      setSaving(true);
      const response = await teamService.update(team.uuid, {
        name: values.name,
        description: values.description || undefined,
      });
      setTeam(response.data);
      setShowEditModal(false);
      showToast("Team updated successfully", "success");
      await refreshTeams();
    } catch (err) {
      console.error("Failed to update team:", err);
      showToast("Failed to update team", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async () => {
    if (!team || !addMemberForm.email.trim()) return;
    try {
      setAddingMember(true);
      await teamService.addMember(team.uuid, addMemberForm.email.trim(), addMemberForm.role);
      showToast("Member added successfully", "success");
      resetAddMemberState();
      await loadTeam();
      await refreshTeams();
    } catch (err: unknown) {
      console.error("Failed to add member:", err);
      const apiErr = err as { data?: { detail?: string } };
      const detail = apiErr?.data?.detail;
      showToast(detail || "Failed to add member", "error");
    } finally {
      setAddingMember(false);
    }
  };

  const handleUpdateRole = async (member: TeamMember, newRole: string) => {
    if (!team) return;
    try {
      setUpdatingRole(member.userUuid);
      setRoleDropdownOpen(null);
      await teamService.updateMemberRole(team.uuid, member.userUuid, newRole);
      showToast(`Role updated to ${getRoleConfig(newRole).label}`, "success");
      await loadTeam();
    } catch (err: unknown) {
      console.error("Failed to update role:", err);
      const apiErr = err as { data?: { detail?: string } };
      const detail = apiErr?.data?.detail;
      showToast(detail || "Failed to update role", "error");
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleRemoveMember = async (member: TeamMember) => {
    if (!team) return;
    setRoleDropdownOpen(null);
    const confirmed = await confirm({
      title: "Remove Member",
      message: `Are you sure you want to remove ${member.name || member.email} from this team?`,
      confirmText: "Remove",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await teamService.removeMember(team.uuid, member.userUuid);
      showToast("Member removed successfully", "success");
      await loadTeam();
      await refreshTeams();
    } catch (err: unknown) {
      console.error("Failed to remove member:", err);
      const apiErr = err as { data?: { detail?: string } };
      const detail = apiErr?.data?.detail;
      showToast(detail || "Failed to remove member", "error");
    }
  };

  const handleDeleteTeam = async () => {
    if (!team) return;
    const confirmed = await confirm({
      title: "Delete Team",
      message: "Are you sure you want to delete this team? This action cannot be undone.",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await teamService.delete(team.uuid);
      showToast("Team deleted successfully", "success");
      await refreshTeams();
      navigate("/teams");
    } catch (err) {
      console.error("Failed to delete team:", err);
      showToast("Failed to delete team", "error");
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-content-primary mb-2">Team not found</h2>
        <button
          onClick={() => navigate("/teams")}
          className="mt-4 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors"
        >
          Back to Teams
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 min-w-0">
      {/* Header */}
      <EntityDetailHeader
        onBack={() => navigate("/teams")}
        icon={Users}
        name={team.name}
        description={team.description}
        badge={
          team.isEveryone && (
            <span className="px-2 py-0.5 text-xs font-medium bg-brand-primary/20 text-brand-primary rounded-full shrink-0">
              Everyone
            </span>
          )
        }
        actions={
          !team.isEveryone && (
            <>
              <button
                type="button"
                onClick={() => {
                  setEditForm({ name: team.name, description: team.description || "" });
                  setShowEditModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg font-medium transition-colors whitespace-nowrap"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </button>
              <button
                type="button"
                onClick={() => setShowAddMemberModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity whitespace-nowrap"
              >
                <UserPlus className="h-4 w-4" />
                Add Member
              </button>
            </>
          )
        }
      >
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-content-quaternary">Members:</span>
            <span className="text-content-primary">{team.members.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-content-quaternary">Created:</span>
            <span className="text-content-primary">{new Date(team.createdAt).toLocaleDateString()}</span>
          </div>
          {team.tokenLimit != null && (
            <div className="flex items-center gap-2">
              <span className="text-content-quaternary">Token Usage:</span>
              <span className="text-content-primary">
                {team.tokensUsed.toLocaleString()} / {team.tokenLimit.toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </EntityDetailHeader>
      {/* Members Table */}
      <div className="flex-1 overflow-auto p-6">
        {team.members.length > 0 ? (
          <div className="bg-surface-primary border border-border-primary rounded-lg">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-primary bg-surface-primary/50">
                  <th className="text-left px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                    Member
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                    Role
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                    Joined
                  </th>
                  {!team.isEveryone && (
                    <th className="text-right px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {team.members.map((member) => {
                  const roleConfig = getRoleConfig(member.role);
                  const RoleIcon = roleConfig.icon;
                  const isUpdating = updatingRole === member.userUuid;

                  return (
                    <tr key={member.userUuid} className="hover:bg-interactive-hover transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-linear-to-br from-brand-primary/30 to-brand-secondary/30 flex items-center justify-center shrink-0">
                            <span className="text-sm font-medium text-content-primary">
                              {(member.name || member.email).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-content-primary truncate">
                              {member.name || "Unnamed User"}
                            </p>
                            <p className="text-xs text-content-tertiary truncate">{member.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {isUpdating ? (
                          <Loader2 className="h-4 w-4 animate-spin text-content-tertiary" />
                        ) : (
                          <div className="relative">
                            {team.isEveryone ? (
                              <div
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ${roleConfig.bg}`}
                              >
                                <RoleIcon className={`h-3 w-3 ${roleConfig.color}`} />
                                <span className={`text-xs font-medium ${roleConfig.color}`}>{roleConfig.label}</span>
                              </div>
                            ) : (
                              <button
                                onClick={() =>
                                  setRoleDropdownOpen(roleDropdownOpen === member.userUuid ? null : member.userUuid)
                                }
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ${roleConfig.bg} hover:opacity-80 transition-opacity`}
                              >
                                <RoleIcon className={`h-3 w-3 ${roleConfig.color}`} />
                                <span className={`text-xs font-medium ${roleConfig.color}`}>{roleConfig.label}</span>
                                <ChevronDown className={`h-3 w-3 ${roleConfig.color}`} />
                              </button>
                            )}

                            {/* Role dropdown */}
                            {roleDropdownOpen === member.userUuid && (
                              <>
                                <div
                                  className="fixed inset-0 z-30"
                                  role="presentation" // NOSONAR — decorative backdrop element
                                  onClick={() => setRoleDropdownOpen(null)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") setRoleDropdownOpen(null);
                                  }}
                                />
                                <div className="absolute top-full left-0 mt-1 bg-surface-primary border border-border-primary rounded-lg shadow-xl py-1 min-w-[200px] z-40">
                                  {ROLE_OPTIONS.map((option) => {
                                    const OptionIcon = option.icon;
                                    const isActive = member.role === option.value;
                                    return (
                                      <button
                                        key={option.value}
                                        onClick={() => {
                                          if (isActive) {
                                            setRoleDropdownOpen(null);
                                          } else {
                                            handleUpdateRole(member, option.value);
                                          }
                                        }}
                                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                          isActive
                                            ? "bg-interactive-hover text-content-primary"
                                            : "text-content-secondary hover:bg-interactive-hover"
                                        }`}
                                      >
                                        <OptionIcon className="h-4 w-4" />
                                        <div className="text-left">
                                          <p className="font-medium">{option.label}</p>
                                          <p className="text-xs text-content-quaternary">{option.description}</p>
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-content-tertiary">
                        {new Date(member.joinedAt).toLocaleDateString()}
                      </td>
                      {!team.isEveryone && (
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end">
                            <button
                              onClick={() => handleRemoveMember(member)}
                              className="p-2 text-content-tertiary hover:text-red-400 hover:bg-interactive-active rounded-sm transition-colors opacity-0 group-hover:opacity-100"
                              title="Remove member"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center mb-4">
              <Users className="h-8 w-8 text-brand-primary" />
            </div>
            <h3 className="text-lg font-medium text-content-primary">No members yet</h3>
            <p className="text-content-tertiary mt-1 text-center max-w-sm">
              {team.isEveryone
                ? "Members are automatically added to this team when they join the platform."
                : "Add members to this team to start collaborating."}
            </p>
            {!team.isEveryone && (
              <button
                onClick={() => setShowAddMemberModal(true)}
                className="mt-6 flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                <UserPlus className="h-4 w-4" />
                Add Member
              </button>
            )}
          </div>
        )}

        {/* Danger zone - only for non-Everyone teams */}
        {!team.isEveryone && (
          <div className="mt-8 border border-red-500/30 rounded-lg p-6">
            <h3 className="text-lg font-medium text-red-400 mb-2">Danger Zone</h3>
            <p className="text-sm text-content-tertiary mb-4">
              Deleting this team will remove all team associations. This action cannot be undone.
            </p>
            <button
              onClick={handleDeleteTeam}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg font-medium transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete Team
            </button>
          </div>
        )}
      </div>
      {/* Edit Team Modal */}
      {showEditModal && (
        <TeamFormModal
          mode="edit"
          initialValues={editForm}
          busy={saving}
          onSubmit={handleEditTeam}
          onClose={() => setShowEditModal(false)}
        />
      )}
      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-border-primary">
              <h2 className="text-lg font-semibold text-content-primary">Add Member</h2>
              <button
                onClick={resetAddMemberState}
                disabled={addingMember}
                className="p-1 text-content-tertiary hover:text-content-primary transition-colors disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label htmlFor="search-user-input" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Search User <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  {selectedUser ? (
                    <div className="flex items-center gap-3 w-full px-3 py-2 bg-surface-secondary border border-brand-primary/50 rounded-lg">
                      <div className="h-8 w-8 rounded-full bg-brand-primary/20 flex items-center justify-center shrink-0">
                        <span className="text-sm font-medium text-brand-primary">
                          {(selectedUser.name || selectedUser.email).charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-content-primary truncate">{selectedUser.name}</p>
                        <p className="text-xs text-content-tertiary truncate">{selectedUser.email}</p>
                      </div>
                      <button
                        onClick={clearSelectedUser}
                        disabled={addingMember}
                        className="p-1 text-content-tertiary hover:text-content-primary transition-colors disabled:opacity-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
                        <input
                          id="search-user-input"
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search by name or email..."
                          value={userSearchQuery}
                          onChange={(e) => {
                            setUserSearchQuery(e.target.value);
                            setAddMemberForm({ ...addMemberForm, email: e.target.value });
                          }}
                          onFocus={() => {
                            if (userSearchResults.length > 0) setShowUserDropdown(true);
                          }}
                          className="w-full pl-9 pr-9 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                        />
                        {isSearchingUsers && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary animate-spin" />
                        )}
                      </div>
                      {showUserDropdown && userSearchResults.length > 0 && (
                        <div
                          ref={dropdownRef}
                          className="absolute left-0 right-0 top-full mt-1 bg-surface-primary border border-border-primary rounded-lg shadow-lg overflow-hidden z-10 max-h-48 overflow-y-auto"
                        >
                          {userSearchResults.map((result) => (
                            <button
                              key={result.uuid}
                              onClick={() => handleSelectUser(result)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-hover transition-colors text-left"
                            >
                              <div className="h-8 w-8 rounded-full bg-brand-primary/20 flex items-center justify-center shrink-0">
                                <span className="text-sm font-medium text-brand-primary">
                                  {(result.name || result.email).charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-content-primary truncate">{result.name}</p>
                                <p className="text-xs text-content-tertiary truncate">{result.email}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {showUserDropdown &&
                        userSearchQuery.length >= 2 &&
                        !isSearchingUsers &&
                        userSearchResults.length === 0 && (
                          <div className="absolute left-0 right-0 top-full mt-1 bg-surface-primary border border-border-primary rounded-lg shadow-lg p-3 z-10">
                            <p className="text-sm text-content-tertiary text-center">
                              No users found matching "{userSearchQuery}"
                            </p>
                          </div>
                        )}
                    </>
                  )}
                </div>
                <p className="mt-1 text-xs text-content-quaternary">
                  Type at least 2 characters to search for platform users.
                </p>
              </div>
              <div>
                <span className="block text-sm font-medium text-content-secondary mb-1.5">Role</span>
                <div className="space-y-2">
                  {ROLE_OPTIONS.map((option) => {
                    const OptionIcon = option.icon;
                    const isSelected = addMemberForm.role === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setAddMemberForm({ ...addMemberForm, role: option.value })}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors text-left ${
                          isSelected
                            ? "border-brand-primary bg-brand-primary/10"
                            : "border-border-secondary hover:border-border-hover bg-surface-secondary"
                        }`}
                      >
                        <OptionIcon
                          className={`h-4 w-4 ${isSelected ? "text-brand-primary" : "text-content-tertiary"}`}
                        />
                        <div>
                          <p
                            className={`text-sm font-medium ${isSelected ? "text-brand-primary" : "text-content-primary"}`}
                          >
                            {option.label}
                          </p>
                          <p className="text-xs text-content-quaternary">{option.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
              <button
                onClick={resetAddMemberState}
                disabled={addingMember}
                className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                disabled={addingMember || !addMemberForm.email.trim()}
                className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingMember ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    Adding...
                  </>
                ) : (
                  "Add Member"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
